import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Input,
  Text,
  Heading,
  List,
  ListItem,
  IconButton,
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { SendHorizonal, UserRound } from 'lucide-react';
import { AttachmentIcon } from '@chakra-ui/icons';
import ReusableInput from 'components/Inputs/ReusableInput';
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import { fetchUsersAction } from '../../../state/actions/userActions';
import { createChatAction, getChatBetweenUsersAction } from '../../../state/actions/chatActions';

const headerBgImage = require('../../../assets/img/back.png');
const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/`.replace(/\/+$/, "/");

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [messageCounts, setMessageCounts] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const userId = useSelector((state) => state.auth?.user?.user?.user_id);
  const { users } = useSelector((state) => state.users);
  const { chat, loading, error } = useSelector((state) => state.chat);

  useEffect(() => {
    dispatch(fetchUsersAction());
  }, [dispatch]);

  const formatMessages = useCallback((chatData, contact) => {
    if (!chatData || !contact) return [];

    return chatData.map((chatItem) => ({
      id: chatItem.chat_id,
      text: chatItem.message,
      sender: chatItem.sender_id === userId ? 'You' : contact?.username,
      createdAt: chatItem.created_at,
      isYou: chatItem.sender_id === userId,
      chatType: chatItem.chat_type,
      receiverId: chatItem.receiver_id,
      senderId: chatItem.sender_id,
      updatedAt: chatItem.updated_at,
      images: (chatItem.images || []).map(img => ({
        url: img.file_path ? BASE_URL + img.file_path.replace(/^\//, '') : img.uri,
        name: img.name || img.fileName,
      })),
      documents: (chatItem.documents || []).map(doc => ({
        url: doc.file_path ? BASE_URL + doc.file_path.replace(/^\//, '') : doc.uri,
        name: doc.name || doc.fileName,
        document_type: doc.document_type,
      })),
    }));
  }, [userId]);

  useEffect(() => {
    if (selectedContact) {
      dispatch(getChatBetweenUsersAction(selectedContact.user_id));
    }
  }, [dispatch, selectedContact]);

  useEffect(() => {
    if (!selectedContact || !chat) return;
    const newUnreadCounts = { ...unreadCounts };
    newUnreadCounts[selectedContact.user_id] = 0;
    setUnreadCounts(newUnreadCounts);

    if (Array.isArray(chat)) {
      const countByUser = { ...messageCounts };
      countByUser[selectedContact.user_id] = chat.length;
      setMessageCounts(countByUser);
    }
  }, [chat, selectedContact]);

  useEffect(() => {
    if (!users || !Array.isArray(users)) return;
    const initialCounts = {};
    users.forEach(user => {
      initialCounts[user.user_id] = unreadCounts[user.user_id] || 0;
    });
    setUnreadCounts(initialCounts);
  }, [users]);

  useEffect(() => {
    if (!selectedContact || !Array.isArray(chat)) return;
    const formattedMessages = formatMessages(chat, selectedContact);
    setMessages(formattedMessages);
  }, [chat, selectedContact, formatMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const userData = Array.isArray(users) ? users : [];
  const categories = ['All', 'Individual', 'Business', 'Driver', 'Agent', 'Admin'];
  const categoryMessageCounts = categories.reduce((acc, category) => {
    if (category === 'All') {
      acc[category] = Object.values(messageCounts).reduce((a, b) => a + b, 0);
    } else {
      const filteredUsers = userData.filter(user => user.user_type === category);
      acc[category] = filteredUsers.reduce((total, user) => {
        return total + (messageCounts[user.user_id] || 0);
      }, 0);
    }
    return acc;
  }, {});

  const filteredContacts = Array.isArray(users) ? users.filter(user => {
    const username = user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const matchesCategory = activeCategory === 'All' || user.user_type === activeCategory;
    const matchesSearchQuery = username.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearchQuery;
  }) : [];

  const handleSendMessage = async () => {
    if (!inputValue.trim() && attachments.length === 0) return;

    const timestamp = new Date().toISOString();
    const tempMessages = [];
    attachments.forEach((file) => {
      tempMessages.push({
        id: Date.now().toString() + Math.random(),
        senderId: userId,
        receiverId: selectedContact.user_id,
        status: "sending",
        createdAt: timestamp,
        type: file.type.startsWith("image/") ? "image" : "document",
        preview: file.preview,   
        fileObject: file.rawFile || file.file,
        name: file.name,
      });
    });

    if (inputValue.trim()) {
      tempMessages.push({
        id: Date.now().toString(),
        senderId: userId,
        receiverId: selectedContact.user_id,
        status: "sending",
        createdAt: timestamp,
        type: "text",
        text: inputValue.trim(),
      });
    }

    setMessages((prev) => [...prev, ...tempMessages]);
    setInputValue('');
    setAttachments([]);

    const formData = new FormData();
    formData.append("sender_id", userId);
    formData.append("receiver_id", selectedContact.user_id);
    formData.append("chat_type", "Admin");
    formData.append("message", inputValue.trim() || " ");

    tempMessages
      .filter(m => m.type === "image")
      .forEach((img) => formData.append("images", img.fileObject));

    tempMessages
      .filter(m => m.type === "document")
      .forEach((doc) => formData.append("documents", doc.fileObject));

    try {
      const response = await dispatch(createChatAction(formData));

      if (response.success) {
        const responseData = Array.isArray(response.data) ? response.data : [response.data];
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.status === "sending") {
              const sentMsg = responseData.find((d) => d.id === msg.id) || {
                ...msg,
                status: "sent",
              };
              return { ...msg, ...sentMsg, status: "sent" };
            }
            return msg;
          })
        );
      } else {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.status === "sending" ? { ...msg, status: "failed" } : msg
          )
        );
        alert(response.error || "Failed to send message");
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.status === "sending" ? { ...msg, status: "failed" } : msg
        )
      );
      alert("Failed to send message");
    }
  };


  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map((file) => ({
      name: file.name,
      type: file.type,
      rawFile: file,
      preview: URL.createObjectURL(file),
    }));
    setAttachments([...attachments, ...newAttachments]);
  };



  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setMessages([]);
  };


  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    onOpen();
  };

  const sortedMessages = [...messages].sort((a, b) =>
    new Date(a.createdAt) - new Date(b.createdAt)
  );

  if (loading) {
    return (
      <Flex maxW="1000px" mx="auto" my="4" mt={20} flexDirection="column" gap={4}>
        <Box><Skeleton height="40px" width="100%" /></Box>
        <Flex>
          <Box width="30%" p="4">
            <Skeleton height="30px" width="80%" mb={4} />
            <Skeleton height="40px" width="100%" mb={2} />
            {Array(5).fill(0).map((_, i) => <Skeleton key={i} height="50px" width="100%" mb={2} />)}
          </Box>
          <Box flex="1" p="4" ml="4" display="flex" flexDirection="column" gap={4}>
            <Skeleton height="30px" width="50%" />
            <Box flex="1">{Array(6).fill(0).map((_, i) => <Skeleton key={i} height="40px" width="100%" mb={2} />)}</Box>
            <Flex gap={2}>
              <Skeleton height="40px" width="100%" flex="1" />
              <Skeleton height="40px" width="40px" />
              <Skeleton height="40px" width="80px" />
            </Flex>
          </Box>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex maxW="1000px" mx="auto" my="4" borderRadius="md" boxShadow="md" mt={20} flexDirection="column">
      {/* Categories */}
      <Box border="1px" borderColor="gray.200" p="4" borderRadius="md" mb="4">
        <Flex justify="start" ml={2}>
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setActiveCategory(category)}
              bg={activeCategory === category ? '#071D54' : 'transparent'}
              color={activeCategory === category ? 'white' : '#071D54'}
              _hover={{ bg: '#071D54', color: 'white' }}
              borderRadius="full"
              border="1px solid #071D54"
              px={6}
              mr={2}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Text>{category}</Text>
              {categoryMessageCounts[category] > 0 && (
                <Box
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  minW="20px"
                  h="20px"
                  aspectRatio={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                >
                  {categoryMessageCounts[category]}
                </Box>
              )}
            </Button>
          ))}
        </Flex>
      </Box>

      <Flex>
        {/* Contacts List */}
        <Box
          width="30%"
          p="4"
          bgImage={`url(${headerBgImage})`}
          color="white"
          borderRadius="md"
          overflowY="auto"
          maxHeight="400px"
        >
          <Heading size="md" mb="4">Chats</Heading>
          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="gray.100"
            color="black"
            mb="4"
          />
          {filteredContacts.length === 0 ? (
            <NoDataFound message="No chats found" textColor={"white"} height={"90px"} width={"90px"} />
          ) : (
            <List spacing={2}>
              {filteredContacts.map((contact) => (
                <ListItem
                  key={contact.user_id}
                  cursor="pointer"
                  onClick={() => handleSelectContact(contact)}
                  bg={selectedContact?.user_id === contact.user_id ? '#1E3A8A' : 'transparent'}
                  p="2"
                  borderRadius="md"
                  _hover={{ bg: '#1E3A8A' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Flex align="center" gap={2}>
                    <Icon
                      as={UserRound}
                      boxSize={7}
                      color="white"
                      bg="#071D54"
                      borderRadius="full"
                      p={1}
                      stroke="none"
                      fill="currentColor"
                    />
                    <Text>{contact.username}</Text>
                  </Flex>
                  {unreadCounts[contact.user_id] > 0 && (
                    <Box
                      bg="red.500"
                      color="white"
                      borderRadius="full"
                      w="20px"
                      h="20px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="xs"
                    >
                      {messageCounts[contact.user_id] || 0}
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Chat Window */}
        <Box
          flex="1"
          p="4"
          borderRadius="md"
          bg="white"
          ml="4"
          display="flex"
          flexDirection="column"
          maxHeight="400px"
        >
          <Heading size="md" mb="4">
            {selectedContact ? `Chat with ${selectedContact.username}` : 'Select a contact'}
          </Heading>

          <Box
            flex="1"
            overflowY="auto"
            mb="4"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            p="2"
          >
            {sortedMessages.length > 0 ? (
              <>
                {sortedMessages.map((msg) => (
                  <Flex key={msg.id} mb="2" justify={msg.isYou ? 'flex-end' : 'flex-start'}>
                    {!msg.isYou && (
                      <Icon
                        as={UserRound}
                        boxSize={7}
                        color="white"
                        bg="#071D54"
                        borderRadius="full"
                        p={1}
                        stroke="none"
                        fill="currentColor"
                        mr={"2"}
                      />
                    )}
                    <Box
                      p="3"
                      borderRadius="md"
                      bg={msg.isYou ? '#1E3A8A' : '#F0F0F0'}
                      color={msg.isYou ? 'white' : 'black'}
                      maxW="70%"
                      wordBreak="break-word"
                    >
                      <Text>{msg.text}</Text>

                      {/* Images */}
                      {msg.images.map((img, i) => (
                        <Box key={i} borderRadius="md" overflow="hidden" maxW="150px" mt={2} cursor="pointer">
                          <img
                            src={img.url}
                            alt={img.name || 'image'}
                            style={{ width: '100%', borderRadius: '6px' }}
                            onClick={() => openImageModal(img.url)}
                          />
                        </Box>
                      ))}

                      {/* Chat Image Documents */}
                      {msg.documents
                        .filter(doc => doc.document_type === 'Chat Image')
                        .map((doc, i) => (
                          <Box key={i} borderRadius="md" overflow="hidden" maxW="150px" mt={2} cursor="pointer">
                            <img
                              src={doc.url}
                              alt={doc.name || 'image'}
                              style={{ width: '100%', borderRadius: '6px' }}
                              onClick={() => openImageModal(doc.url)}
                            />
                          </Box>
                        ))}

                      <Text fontSize="xs" color={msg.isYou ? 'gray.300' : 'gray.500'} mt={1}>
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </Text>
                    </Box>
                    {msg.isYou && (
                      <Icon
                        as={UserRound}
                        boxSize={7}
                        color="white"
                        bg="#071D54"
                        borderRadius="full"
                        p={1}
                        stroke="none"
                        fill="currentColor"
                        ml={"2"}
                      />
                    )}
                  </Flex>
                ))}
                <div ref={messagesEndRef} />
              </>
            ) : (
              <Flex height="100%" align="center" justify="center" textAlign="center" mt={10}>
                <Text color="gray.500">
                  {selectedContact
                    ? "No messages yet. Start the conversation!"
                    : "Select a contact to start chatting"}
                </Text>
              </Flex>
            )}
          </Box>

          {selectedContact && (
            <Flex>
              <ReusableInput
                placeholder="Type your message here..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                bg="gray.100"
                color="black"
                mr="2"
              />
              <input
                type="file"
                id="file-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <IconButton
                as="label"
                htmlFor="file-upload"
                aria-label="Attach files"
                icon={<AttachmentIcon color="#1E3A8A" />}
                variant="ghost"
                mr="2"
              />
              <Button
                onClick={handleSendMessage}
                isDisabled={!inputValue.trim()}
                bg="#071D54"
                color="white"
                borderRadius="md"
                _hover={{ bg: '#052050' }}
                rightIcon={<SendHorizonal />}
              />
            </Flex>
          )}
        </Box>
      </Flex>

      {/* Image Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" />
          <ModalBody p={0}>
            <img src={selectedImage} alt="Preview" style={{ width: '100%', borderRadius: '8px' }} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default Chat;
